/**
 * "Today's decisions" — the runtime event feed. Row grammar from the mock:
 * Danish time · tone dot on a connector rail · bold headline · muted why.
 * Every decision carries its reason; unknown event kinds render neutral.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Clock } from 'lucide-react';

import type { DropGenEventRow } from '@trafficpulse/shared';

import { useVisibleEvents } from '../shared/experiment/use-can-see-experiments';
import { eventToneColor, feedTime } from './feed-meta';

interface Props {
  events: DropGenEventRow[] | undefined;
  loading: boolean;
  failed: boolean;
}

export const DecisionsFeed: FC<Props> = ({ events: all, loading, failed }) => {
  // Experiment lines only for whoever may see experiments (admin-only unless granted, 14/10/2026).
  const events = useVisibleEvents(all);
  return (
  <div className="flex flex-col gap-3 rounded-lg border border-border/60 bg-card p-4">
    <div className="flex items-center gap-2 border-b border-border/60 pb-2">
      <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Clock className="h-3.5 w-3.5" /> Today&apos;s decisions
      </h3>
      <span className="grow" />
      <span className="text-xs text-muted-foreground">
        every one carries its reason
      </span>
    </div>
    {loading ? (
      <div className="flex flex-col gap-1.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-8 animate-pulse rounded-md bg-muted/60" />
        ))}
      </div>
    ) : failed && (!events || events.length === 0) ? (
      <p className="text-sm text-muted-foreground">feed unavailable</p>
    ) : !events || events.length === 0 ? (
      <p className="text-sm text-muted-foreground">
        No decisions recorded yet — Generate the day and the story starts
        here.
      </p>
    ) : (
      <>
      {failed ? (
        <p className="text-xs text-muted-foreground">
          updates paused — retrying
        </p>
      ) : null}
      <ul className="flex max-h-96 flex-col overflow-y-auto pr-1">
        {events.map((event, index) => (
          <li
            key={event.id}
            className="grid grid-cols-[52px_20px_1fr] items-start gap-x-1 pb-2.5"
          >
            <span className="pt-px text-right font-mono text-[11px] tabular-nums text-muted-foreground">
              {feedTime(event.at)}
            </span>
            <span className="relative flex h-full justify-center">
              <span
                className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: eventToneColor(event.event) }}
              />
              {index < events.length - 1 ? (
                <span className="absolute top-4 bottom-[-10px] w-0.5 bg-border/60" />
              ) : null}
            </span>
            <span className="min-w-0">
              <span className="block text-[13px] font-medium">
                {event.headline}
              </span>
              {event.reason ? (
                <span className="block text-xs text-muted-foreground">
                  {event.reason}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      </>
    )}
  </div>
  );
};
